import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme, usePageTitle, AlertBanner } from '@flowcore-water/ui';
import { fetchEntityList, type PaginatedResponse } from '../lib/api';
import { DataTable } from '../components/DataTable';
import { Pagination } from '../components/Pagination';

export function EntityList() {
  const { entity } = useParams<{ entity: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTheme();
  usePageTitle('ST Mirror', `Browse ${entity ?? ''}`);

  const q = searchParams.get('q') || '';
  const sort = searchParams.get('sort') || '';
  const order = searchParams.get('order') || 'desc';
  const page = Number(searchParams.get('page')) || 1;

  const [data, setData] = useState<PaginatedResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState(q);

  const load = useCallback(() => {
    if (!entity) return;
    setLoading(true);
    fetchEntityList(entity, { q: q || undefined, sort: sort || undefined, order, page })
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [entity, q, sort, order, page]);

  useEffect(() => { load(); }, [load]);

  function updateParams(updates: Record<string, string | undefined>) {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(updates)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    // Reset to page 1 when search or sort changes
    if ('q' in updates || 'sort' in updates || 'order' in updates) {
      next.delete('page');
    }
    setSearchParams(next);
  }

  function handleSort(col: string) {
    const newOrder = sort === col && order === 'asc' ? 'desc' : 'asc';
    updateParams({ sort: col, order: newOrder });
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    updateParams({ q: search || undefined });
  }

  if (!entity) return null;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold tracking-tight" style={{ color: t.textPrimary }}>
            {data?.display ?? entity}
          </h1>
          {data && (
            <p className="mt-0.5 text-xs" style={{ color: t.textMuted }}>
              {data.total.toLocaleString()} records
            </p>
          )}
        </div>
        <form onSubmit={handleSearch} className="flex gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${entity}...`}
            className="w-64 rounded-lg border px-3 py-1.5 text-sm outline-none"
            style={{
              background: t.inputBg,
              borderColor: t.inputBorder,
              color: t.textPrimary,
            }}
          />
          <button
            type="submit"
            className="rounded-lg border px-3 py-1.5 text-sm font-medium"
            style={{ background: t.buttonBg, borderColor: t.buttonBorder, color: t.buttonText }}
          >
            Search
          </button>
          {q && (
            <button
              type="button"
              onClick={() => { setSearch(''); updateParams({ q: undefined }); }}
              className="rounded-lg border px-3 py-1.5 text-sm"
              style={{ borderColor: t.borderSubtle, color: t.textMuted }}
            >
              Clear
            </button>
          )}
        </form>
      </div>

      {error && <AlertBanner variant="error" message={error} />}

      {loading && !data && (
        <div className="flex h-48 items-center justify-center">
          <span style={{ color: t.textMuted }}>Loading...</span>
        </div>
      )}

      {data && (
        <>
          <DataTable
            columns={data.data.length > 0 ? Object.keys(data.data[0]) : []}
            rows={data.data}
            sortColumn={data.sort}
            sortOrder={data.order}
            onSort={handleSort}
            onRowClick={(row) => navigate(`/browse/${entity}/${row.id}`)}
          />
          <Pagination
            page={data.page}
            pages={data.pages}
            total={data.total}
            onPageChange={(p) => updateParams({ page: String(p) })}
          />
        </>
      )}
    </div>
  );
}
